import { computed, ref } from 'vue';
import { defineStore, getActivePinia } from 'pinia';
import { compareTierAndPlan } from '@/utils/membershipUtility';
import type { ResetPasswordBeginPayload, ResetPasswordValidateTokenPayload } from "@/services/authApiService";
import {
  AuthApiService,
  type LoginPayload,
  type ResetPasswordPayload,
  type SignUpPayload,
} from "@/services/authApiService";
import type { User } from "@/models/user";
import {
  UserApiService,
  type UserUpdateEmailPayload,
  type UserUpdatePasswordPayload,
  type UserUpdatePayload,
} from "@/services/userApiService";
import router from "@/router";
import type { RouteRecordName } from "vue-router";
import { clientScope } from "@/utils/apiClient";
import {
  type CreateMembershipPayload,
  MembershipApiService,
  type UpdateMembershipPayload,
} from "@/services/membershipApiService";
import type { Membership } from "@/models/membership";
import { MembershipImportStatus, MembershipStatus } from "@/models/membership";
import { useAppStore } from "@/stores/app";
import * as amplitude from '@amplitude/analytics-browser';
import { useCardsStore } from "@/stores/cards";

export const DEFAULT_LOGIN_REDIRECT_ROUTE_NAME = 'home';

export const useSessionStore = defineStore('session', () => {
  // State
  const needsInit = ref(true);

  const language = ref<'en' | 'fr' | 'es' | 'de' | 'it'>('en');
  const cookiesAccepted = ref<boolean>(false);

  const isLogged = ref(false);
  const loggedUser = ref<User | null>(null);
  const loginRedirectRouteName = ref<RouteRecordName>(DEFAULT_LOGIN_REDIRECT_ROUTE_NAME);

  const membership = ref<Membership | null>(null);
  const lotteryNumbers = ref<string[]>([]);

  // Getters
  const isMember = computed(() => loggedUser.value?.isMember ?? false);
  const isFixedMembershipTier = computed(() => {
    const appStore = useAppStore();
    const membershipTier = appStore.membershipTiers.find(o => o.id === membership.value?.membershipTierId);
    return !!membershipTier && membershipTier.price !== -1;
  });
  const canSeeVideosSection = computed(() => {
    const appStore = useAppStore();
    return appStore.client?.featureFlags.videosPage && appStore.client?.vimeoShowcaseId && !!currentSubscription;
  });
  const mustActivate = computed(() => {
    return membership.value?.membershipTierId && membership.value?.importStatus === MembershipImportStatus.INCOMPLETE;
  });

  // Subscription
  const lastSubscription = computed(() => {
    if (!membership.value?.membershipSubscriptions || membership.value?.membershipSubscriptions.length === 0) {
      return null;
    }
    return membership.value?.membershipSubscriptions[ membership.value?.membershipSubscriptions.length - 1 ] ?? null;
  });
  const currentSubscription = computed(() => {
    return membership.value?.currentSubscription;
  });
  const currentTier = computed(() => {
    const appStore = useAppStore();
    return appStore.membershipTiers.find(t => t.id === currentSubscription.value?.membershipTierId);
  });
  const currentTierPlan = computed(() => {
    return currentTier.value?.membershipTierPlans.find(tp => tp.id === currentSubscription.value?.membershipTierPlanId);
  });
  const membershipTier = computed(() => {
    const appStore = useAppStore();
    return appStore.membershipTiers.find(o => o.id === membership.value?.membershipTierId);
  });
  const membershipTierPlan = computed(() => {
    return membershipTier.value?.membershipTierPlans.find(o => o.id === membership.value?.membershipTierPlanId);
  });
  const isDowngradeScheduled = computed(() => {
    if (!currentTier.value || !membershipTier.value) {
      return false;
    }

    return compareTierAndPlan(currentTier.value, membershipTier.value, currentTierPlan.value, membershipTierPlan.value) === -1;
  });

  // Churned with an active subscription
  const isActiveChurned = computed(() => {
    return membership.value?.status === MembershipStatus.CHURNED && currentSubscription.value !== null;
  });

  // Actions
  function initLogin(routeName: RouteRecordName | null | undefined): void {
    loginRedirectRouteName.value = routeName ?? DEFAULT_LOGIN_REDIRECT_ROUTE_NAME;
  }

  async function init(): Promise<void> {
    if (needsInit.value && !isLogged.value) {
      await AuthApiService.setCsrfTokenCookie();
      let user = null;
      let lotteryFormattedNumbers: string[] = [];
      try {
        const response = await UserApiService.get();
        user = response.data?.user ?? null;
        lotteryFormattedNumbers = response.data?.lotteryTicketNumbers ?? [];
        membership.value = response.data?.membership ?? null;
      } catch (e) {
      }
      await setUser(user);
      lotteryNumbers.value = lotteryFormattedNumbers;
      needsInit.value = false;
    }
  }

  async function signUp(payload: SignUpPayload): Promise<void> {
    await AuthApiService.signUp(payload);
    const response = await UserApiService.get();
    const user = response.data?.user ?? null;
    if (user) {
      amplitude.track('Signed up');
      await setUser(user, true);
    }
    // TODO: else?
  }

  async function enroll(payload: CreateMembershipPayload): Promise<void> {
    if (!loggedUser.value || loggedUser.value?.isMember) {
      return;
    }
    if (payload.newsletter === undefined) {
      payload.newsletter = loggedUser.value?.newsletter;
    }
    try {
      await MembershipApiService.create(payload);
      await refreshUser();
    } catch (e) {
      console.error(e);
    }
  }

  async function login(email: string, password: string) {
    if (!isLogged.value) {
      const payload: LoginPayload = {
        email,
        password,
      };
      await AuthApiService.login(payload);
      // If the api service does not throw an error, the user is logged in

      const response = await UserApiService.get();
      const user = response.data?.user ?? null;
      membership.value = response.data?.membership ?? null;
      const lotteryFormattedNumbers = response.data?.lotteryTicketNumbers ?? [];

      await setUser(user);
      lotteryNumbers.value = lotteryFormattedNumbers;

      if (user) {
        amplitude.track('Logged in');
      }
    }
    const redirectRouteName = loginRedirectRouteName.value;
    loginRedirectRouteName.value = DEFAULT_LOGIN_REDIRECT_ROUTE_NAME;
    return redirectRouteName;
  }

  async function logout(): Promise<void> {
    if (isLogged.value) {
      await AuthApiService.logout();
    }
    await setUser(null);
    membership.value = null;
    lotteryNumbers.value = [];
    await router.push({name: 'home', force: true});
    // Reset stores
    const pinia = getActivePinia();
    if (pinia) {
      // HACK: _s might change in the future
      // @ts-ignore
      pinia._s.forEach((store: any) => store.$reset());
    }
  }

  async function resetPasswordBegin(payload: ResetPasswordBeginPayload): Promise<void> {
    await AuthApiService.resetPasswordBegin(payload);
  }

  async function resetPasswordValidateToken(payload: ResetPasswordValidateTokenPayload): Promise<void> {
    const response = await AuthApiService.resetPasswordValidateToken(payload);
    if (response.data?.token) {
      await router.push({
        name: 'password-reset-2',
        query: {email: response.data.token.email, token: response.data.token.token},
      });
    }
  }

  async function resetPassword(payload: ResetPasswordPayload): Promise<void> {
    const response = await AuthApiService.resetPassword(payload);
    if (response.data?.user) {
      window.location.href = window.location.origin;
    }
  }

  async function refreshUser(): Promise<void> {
    const response = await UserApiService.get();
    const user = response.data?.user ?? null;
    membership.value = response.data?.membership ?? null;
    await setUser(user);
    lotteryNumbers.value = response.data?.lotteryTicketNumbers ?? [];
  }

  async function setUser(user: User | null, isSignUp: boolean = false): Promise<void> {
    loggedUser.value = user;
    isLogged.value = !!user;

    // Identify the user on Amplitude
    if (user) {
      const appStore = useAppStore();
      const membershipTierIndex = appStore.membershipTiers.findIndex(o => o.id === membership.value?.membershipTierId);
      const membershipTier = membershipTierIndex !== -1 ? appStore.membershipTiers[ membershipTierIndex ] : null;
      const membershipTierLevel = membershipTierIndex !== -1 ? membershipTierIndex + 1 : null;

      let subscriptionType = 'Not Subscribed';
      if (user.isSubscribed) {
        if (appStore.isSubscriptionProgram) {
          subscriptionType = isFixedMembershipTier.value ? 'Membership' : 'Roundup';
        } else {
          subscriptionType = 'Roundup';
        }
      }

      const cardsStore = useCardsStore();
      try {
        await cardsStore.fetchCards({withDeleted: true});
      } catch (error) {
        console.error('Failed to fetch cards:', error);
      }

      // Amplitude User Identification
      amplitude.setUserId(user.email);
      const event = new amplitude.Identify();
      event.set('User ID', user.id);
      event.set('Subscription Type', subscriptionType);
      event.set('Tier Name', membershipTier?.name ?? 'N/A');
      event.set('Tier Price', membershipTier?.price ?? 'N/A');
      event.set('Tier Level', membershipTierLevel ?? 'N/A');
      event.postInsert('Client Linked', appStore.client!.name);
      event.set('Referral', membership.value?.referralRockParentId ? 'Yes' : 'No');
      if (isSignUp) {
        event.set('Signup Method', user.oauthProvider ?? 'Email');
      }
      amplitude.identify(event);
    }
  }

  async function updateUser(user: UserUpdatePayload): Promise<void> {
    const response = await UserApiService.update(user);
    const newUser = response.data?.user ?? null;
    setUser(newUser);
  }

  async function updateMembership(payload: UpdateMembershipPayload): Promise<void> {
    const response = await MembershipApiService.update(payload);
    if (loggedUser.value) {
      loggedUser.value!.clientNewsletter = response.data?.membership?.newsletter ?? false;
    }
  }

  async function updateUserEmail(payload: UserUpdateEmailPayload): Promise<void> {
    const response = await UserApiService.updateEmail(payload);
    const newUser = response.data?.user ?? null;
    setUser(newUser);
  }

  async function updateUserPassword(payload: UserUpdatePasswordPayload): Promise<void> {
    const response = await UserApiService.updatePassword(payload);
    const newUser = response.data?.user ?? null;
    setUser(newUser);
  }

  async function loginWithProvider(provider: string, page: string, referralCode?: string): Promise<void> {
    const response = await AuthApiService.loginWithProvider(provider, {
      client: clientScope,
      page,
      referralCode,
    });
    if (response.data?.targetUrl) {
      window.location.href = response.data?.targetUrl!;
    }
  }

  function $reset() {
    // Stub
  }

  return {
    cookiesAccepted,
    language,
    isLogged,
    loggedUser,
    lotteryNumbers,
    membership,
    lastSubscription,
    currentSubscription,
    currentTier,
    currentTierPlan,
    isMember,
    isFixedMembershipTier,
    canSeeVideosSection,
    mustActivate,
    isDowngradeScheduled,
    membershipTier,
    membershipTierPlan,
    isActiveChurned,
    init,
    initLogin,
    signUp,
    login,
    logout,
    enroll,
    loginWithProvider,
    resetPasswordBegin,
    resetPasswordValidateToken,
    resetPassword,
    refreshUser,
    updateUser,
    updateUserEmail,
    updateUserPassword,
    updateMembership,
    $reset,
  };
});